import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { IOpenCartItem } from "@/api_framework/api_modals/DayClosing";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useEffect, useRef } from "react";
import { useDayClosingOpenCartAPI } from "@/app/hooks/api_hooks/dayClosing/useDayClosingOpenCartAPI";
import { useNavigate } from "react-router";

const OpenCartsClosingComponent = () => {
  const { execute: fetchOpenCartData, openCartData } = useDayClosingOpenCartAPI();
  const queryParams = new URLSearchParams(location.search);
  const isLoading = useLoader("isClosingDataInProgress");
  const { NO_APPOINTMENTS } = useImageConstants();
  const navigate = useNavigate();

  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process") ?? "";
  const queryCity = queryParams.get("city_id");
  const time = queryParams.get("t") ?? "";
  const lastRequestKeyRef = useRef("");

  useEffect(() => {
    if (!queryClinics) return;
    const requestKey = `${queryClinics}-${queryShift}-${processId}-${queryCity}-${time}`;
    if (lastRequestKeyRef.current === requestKey) return;
    lastRequestKeyRef.current = requestKey;
    fetchOpenCartData(queryClinics, queryShift);
  }, [fetchOpenCartData, processId, queryCity, queryClinics, queryShift, time]);

  const handleGoToCart = (cart: IOpenCartItem, selectedPetId?: string) => {
    if (!cart?.customer_id || !cart?.cart_id) return;
    const petId = selectedPetId || "";
     navigate(`/app/user-cart/cart-creation/${cart.customer_id}/${cart.cart_id}?from_cart=true&&pet_id=${petId}`);
    console.log(selectedPetId);
  };

  return (
    <div className="w-full">
      {isLoading ? (
        <TableSkeletonComponent columnLength={8} />
      ) : openCartData?.length > 0 ? (
        <div className="space-y-3 overflow-y-auto max-h-[650px]">
          {openCartData?.map((clinic) => (
            <div
              key={clinic?.clinic_details?.clinic_id ?? clinic?.clinic_details?.clinic_name}
              className="rounded-2xl border border-[#ECEEF2] bg-white p-3 shadow-sm"
            >
              <div className="overflow-x-auto rounded-xl">
                <table className="w-full min-w-[1300px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                  <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal">
                    <tr>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        User Name
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Pets' Name
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Clinic Name
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Pet's Appointment Type
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Appointment Date & Time
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Cart Amount
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Cart Status
                      </th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="text-xs">
                    {clinic?.closure_list
                      ?.flatMap((cartData: any) => {
                        const petList = Array.isArray(cartData?.pet_details) ? cartData?.pet_details : [];
                        if (petList.length === 0) {
                          return [{ cartData, pet: null }];
                        }
                        return petList.map((pet: any) => ({ cartData, pet }));
                      })
                      ?.map((row: any, rowIndex: number, arr: any[]) => {
                        const { cartData, pet } = row;
                        const isFirst = rowIndex === 0;
                        const isLast = rowIndex === arr.length - 1;
                        const isSingle = arr.length === 1;

                        return (
                          <tr
                            key={`${cartData?.cart_id}-${pet?.id || pet?.pet_id || rowIndex}`}
                            className={`
                              bg-white hover:bg-[#FCFCFD] transition-colors
                              ${isSingle ? "rounded-xl" : ""}
                              ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                              ${isLast && !isSingle ? "rounded-b-xl" : ""}
                            `}
                          >
                            <td
                              className={`
                                p-3 text-xs whitespace-nowrap
                                ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                                ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                                ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                              `}
                            >
                              {cartData?.customer_details?.name || "-"}
                            </td>

                            <td className="p-3 text-xs whitespace-nowrap">
                              {pet?.name || "-"}
                            </td>

                            <td className="p-3 text-xs whitespace-nowrap">
                              {cartData?.clinic_name || clinic?.clinic_details?.clinic_name || "-"}
                            </td>
                            <td className="p-3 text-xs">
                              <div className="flex flex-col gap-1">
                                <p className="whitespace-nowrap">
                                  {cartData?.booking_details?.service_type || "-"}
                                </p>
                                <p className="whitespace-nowrap text-[#6C727F]">
                                  {cartData?.booking_details?.sub_service_name || "-"}
                                </p>
                              </div>
                            </td>
                            <td className="p-3 text-xs whitespace-nowrap">
                              {cartData?.booking_details?.booking_start_date_time || "-"}
                            </td>
                            <td className="p-3 text-xs whitespace-nowrap">
                              {/* {cartData?.net_unbilled_amount ?? "-"} */}
                                {pet?.net_unbilled_amount ?? "-"}
                            </td>
                            <td className="p-3 py-2 text-xs whitespace-nowrap">
                              <span
                                className={`inline-flex items-center justify-center px-3 py-1 text-xs rounded-full ${
                                  cartData?.status?.toLowerCase() === "billed"
                                    ? "bg-green-100"
                                    : "bg-red-400"
                                }`}
                              >
                                {cartData?.status || "-"}
                              </span>
                            </td>
                            <td
                              className={`
                                p-3 text-xs whitespace-nowrap
                                ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                                ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                                ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                              `}
                            >
                              {!cartData?.closure_status ? (
                                <button
                                  type="button"
                                  onClick={() => handleGoToCart(cartData, pet?.id || pet?.pet_id || "")}
                                  className="bg-black px-4 py-2 rounded-lg text-xs text-white font-medium whitespace-nowrap hover:opacity-90 transition-opacity"
                                >
                                  Go to cart
                                </button>
                              ) : (
                                ""
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Open Carts Found" />
      )}
    </div>
  );
};

export default OpenCartsClosingComponent;
